import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import React from "react";
import { CustomDetailsList, CustomTable, Loader } from "../../../shared";
import { createColumnHelper } from "@tanstack/react-table";
import { formatWeightWithUnits, parseFormattedWeight } from "../../../utils";

export const IngredientsBasicDetails = ({
  state,
  ingredient,
  optionsUnits,
  globalData,
}) => {
  const columnHelper = createColumnHelper();

  const baseColumns = [
    {
      title: "Ingredient Name",
      accessor: (val) => (
        <Typography variant="subtitle2" fontWeight={500}>
          {val.name}
        </Typography>
      ),
    },
    {
      title: "Alternate Name",
      accessor: (val) => val.alternate_name || "-",
    },
    {
      title: "Tax Group",
      accessor: "tax_group_name",
    },
    {
      title: "Type",
      accessor: (val) =>
        globalData?.Ingredient_types?.find(
          (incType) => incType.value === val?.type,
        )?.label || "-",
    },
    {
      title: "Category tags",
      accessor: (val) => {
        return (
          <Stack direction="row" gap="8px">
            {val?.tags?.map((data) => (
              <Chip size="small" variant="outlined" color="gray" label={data} />
            ))}
          </Stack>
        );
      },
    },
    {
      title: "Suppliers",
      accessor: (val) => {
        return (
          <Stack direction="row" gap="8px">
            {val?.suppliers?.map((data) => (
              <Chip size="small" variant="outlined" color="gray" label={data} />
            ))}
          </Stack>
        );
      },
    },
    {
      title: "Buying Unit",
      accessor: (val) => (
        <Typography variant="subtitle2">
          {optionsUnits?.find((c) => c?.id === val?.buying_unit_id)?.name}
        </Typography>
      ),
    },
  ];

  const costColumns = [
    {
      title: "Cost (ex GST)",
      accessor: (val) => (
        <Typography variant="subtitle2">
          $
          {Number(val.cost_exc_tax) % 1 === 0
            ? Number(val.cost_exc_tax)
            : Math.floor(Number(val.cost_exc_tax) * 1000) / 1000}
        </Typography>
      ),
    },
    {
      title: "Cost (in GST)",
      accessor: (val) => (
        <Typography variant="subtitle2">
          $
          {Number(val.cost_inc_tax) % 1 === 0
            ? Number(val.cost_inc_tax)
            : Math.floor(Number(val.cost_inc_tax) * 1000) / 1000}
        </Typography>
      ),
    },
  ];

  const columns = [...baseColumns, ...costColumns];
  const columns2 = [
    columnHelper.accessor("base_unit_qty", {
      header: () => "Base Qty",
      cell: ({ row }) => {
        const value = formatWeightWithUnits(
          row?.original?.base_unit_qty,
          row?.original?.base_unit_name,
          globalData?.all_Units,
        );
        const { qty } = parseFormattedWeight(value);
        return <Stack direction="row">{qty}</Stack>;
      },
    }),
    columnHelper.accessor("base_unit_name", {
      header: () => "Base Unit",
      cell: ({ row }) => {
        const value = formatWeightWithUnits(
          row?.original?.base_unit_qty,
          row?.original?.base_unit_name,
          globalData?.all_Units,
        );

        const { unit } = parseFormattedWeight(value);
        return <Stack direction="row">{unit}</Stack>;
      },
    }),
    columnHelper.accessor("id", {
      header: () => "=",
      cell: (info) => "=",
    }),
    columnHelper.accessor("alt_unit_qty", {
      header: () => "Alternative Unit",
      cell: (row) => {
        const { alt_unit_id, alt_unit_qty, alt_unit_name } = row.row.original;

        return (
          <Stack direction="row" gap={1}>
            <Typography>{alt_unit_qty}</Typography>
            <Typography>{alt_unit_name}</Typography>
            {alt_unit_id === ingredient?.data?.use_uom_unit && (
              <Chip
                size="small"
                color="primary"
                variant="filled"
                label="DEFAULT"
              />
            )}
          </Stack>
        );
      },
    }),
  ];

  const variantColumns = React.useMemo(
    () => [{ header: "Variant Name", accessorKey: "name" }],
    [],
  );

  return (
    <>
      {ingredient.isLoading ? (
        <Loader />
      ) : (
        <>
          <CustomDetailsList
            columns={columns}
            data={ingredient?.data}
            title="Basic Details"
            style={{ padding: 0 }}
          />
          {ingredient?.data?.variants?.length > 0 && (
            <Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                p="25px 24px 13px 0px"
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  lineHeight="19px"
                  minWidth="160px"
                >
                  Variants
                </Typography>
              </Stack>
              <Box
                sx={{
                  p: "0 8px 8px",
                  bgcolor: "#F5F8FC",
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <CustomTable
                  defaultData={ingredient?.data?.variants}
                  columns={variantColumns}
                  isPagination={false}
                  isTableHeader={false}
                />
              </Box>
            </Stack>
          )}
          {ingredient.data.has_uom ? (
            <Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                p="25px 24px 13px 0px"
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  lineHeight="19px"
                  minWidth="160px"
                >
                  Alternate Unit Of Measure
                </Typography>
              </Stack>
              <Box
                sx={{
                  p: "0 8px 8px",
                  bgcolor: "#F5F8FC",
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <CustomTable
                  defaultData={ingredient?.data?.units}
                  columns={columns2}
                  isPagination={false}
                  isTableHeader={false}
                />
              </Box>
            </Stack>
          ) : null}
          <Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              p="25px 24px 13px 0px"
            >
              <Typography
                variant="h6"
                fontWeight={600}
                lineHeight="19px"
                minWidth="160px"
              >
                Spec Sheet
              </Typography>
            </Stack>
            <Box
              sx={{
                p: "0 8px 8px",
                bgcolor: "#F5F8FC",
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ width: "100%", padding: "16px" }}>
                <Stack direction="row" spacing={2}>
                  {state.data.pdf.length ? (
                    state.data.pdf.map((file, index) => (
                      <Paper
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          padding: "8px",
                          minWidth: "150px",
                        }}
                      >
                        <img src="/icons/ic_file.jpg" alt="file" />
                        <Box sx={{ marginLeft: "8px", overflow: "hidden" }}>
                          <Typography
                            variant="body2"
                            noWrap
                            component="a"
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              marginTop: "20px",
                              color: "black",
                              cursor: "pointer",
                              textDecoration: "none",
                            }}
                          >
                            {file.doc_name}
                          </Typography>
                        </Box>
                      </Paper>
                    ))
                  ) : (
                    <Stack
                      width="100%"
                      minHeight="100px"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ textAlign: "center" }}
                    >
                      <Typography
                        fontWeight={500}
                        fontSize="14px"
                        color="#485364"
                      >
                        No spec sheet added
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Box>
          </Stack>
          {ingredient?.data?.notes ? (
            <Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                p="25px 24px 13px 0px"
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  lineHeight="19px"
                  minWidth="160px"
                >
                  Notes
                </Typography>
              </Stack>
              <Box
                sx={{
                  p: "0 8px 8px",
                  bgcolor: "#F5F8FC",
                  minHeight: "78px",
                  padding: "16px",
                }}
              >
                <Typography
                  variant="body4"
                  fontWeight={400}
                  fontSize="14px"
                  color="#485364"
                >
                  <Box
                    sx={{
                      whiteSpace: "pre-line",
                      border: "1px solid #ccc",
                      p: 2,
                    }}
                  >
                    {ingredient?.data?.notes}
                  </Box>
                </Typography>
              </Box>
            </Stack>
          ) : null}
        </>
      )}
    </>
  );
};
